import { Box, Stack, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';

import { preferenceClient } from '@portal-clients/preference';
import {
  OnboardingPreferencesModel,
  UserModel,
  userClient,
} from '@portal-clients/user';

import {
  AppBottomActionBar,
  FormikSelectField,
  FormikTextField,
  SubmitButton,
  useToast,
  ViewSection,
} from '@frontend/components';
import { useSubmit } from '@frontend/hooks';
import { tApp, tCommon, useI18n } from '@frontend/locales';
import {
  formUtils,
  routes,
  usePortalConfiguration,
  useUser,
} from '@frontend/utils';

import { NameAndLanguageFormInput } from './name-and-language-form-input.interface';

export const NameAndLanguageStep = () => {
  const { user, updatePreferences } = useUser();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const {
    config: { locales },
  } = usePortalConfiguration();
  const { setLanguage } = useI18n();

  const { submit } = useSubmit<UserModel, NameAndLanguageFormInput>({
    async onSubmit(params) {
      const updatedModel = await userClient.updateActive({
        firstName: formUtils.string.toModel(params.firstName),
        lastName: formUtils.string.toModel(params.lastName),
        locale: params.locale,
      });

      await preferenceClient
        .set('onboarding', {
          preference: {
            onboarded: true,
            version: 1,
            stepsPerformed: ['NAME_AND_LANGUAGE'],
          } satisfies OnboardingPreferencesModel,
        })
        .then(() => {
          updatePreferences({
            onboarding: {
              onboarded: true,
              version: 1,
              stepsPerformed: ['NAME_AND_LANGUAGE'],
            },
          });
        })
        .catch((e) => {
          captureException(e, {
            level: 'warning',
            extra: {
              message: 'Failed to update preferences for onboarding',
            },
          });
        });

      setLanguage(params.locale);

      return updatedModel;
    },
    onError() {
      showToast({
        severity: 'error',
        body: tApp.profile.failedToUpdateProfile(),
      });
    },
    onFinally() {
      navigate(routes.wallet.list(user.accounts[0]?.id));
    },
  });

  const validationSchema = useMemo(
    () =>
      object<NameAndLanguageFormInput>({
        firstName: string(),
        lastName: string(),
      }),
    [],
  );

  const selectOptions = useMemo(() => {
    if (locales) {
      return locales.map((locale) => {
        return {
          id: locale,
          displayName: tApp.languageOptions[locale](),
        };
      });
    }

    return [];
  }, [locales]);

  return (
    <Formik<NameAndLanguageFormInput>
      initialValues={{
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        locale: user.locale,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, helpers) => {
        await submit(values);
        helpers.resetForm({
          values,
        });
      }}
    >
      {() => {
        return (
          <Stack spacing={3} pt={2} pb={2} id="stack">
            <ViewSection sx={{ pt: 'var(--safe-area-inset-top)' }}>
              <Stack alignItems="center" spacing={1} pt={2} pb={2}>
                <Typography variant="h4">
                  {tApp.headers.finaliseProfile()}
                </Typography>

                <Typography variant="body1" color="textSecondary">
                  {tApp.profile.finaliseProfileDescription()}
                </Typography>
              </Stack>
            </ViewSection>

            <Form
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
            >
              <ViewSection>
                <Stack spacing={2} justifyContent="center">
                  <FormikTextField
                    type="text"
                    name="firstName"
                    autoComplete="given-name"
                    label={tApp.profile.firstName()}
                  />

                  <FormikTextField
                    type="text"
                    name="lastName"
                    autoComplete="family-name"
                    label={tApp.profile.lastName()}
                  />

                  {selectOptions.length > 1 && (
                    <FormikSelectField
                      name="locale"
                      autoComplete="language"
                      label={tApp.profile.language()}
                      options={selectOptions}
                    />
                  )}
                </Stack>
              </ViewSection>

              <AppBottomActionBar slideEnabled={false}>
                <Box width="100%">
                  <SubmitButton>{tCommon.actions.continue()}</SubmitButton>
                </Box>
              </AppBottomActionBar>
            </Form>
          </Stack>
        );
      }}
    </Formik>
  );
};
